// Data glue for the engineer's own weekly timesheet (Session AEL, Director decision D41). An
// engineer can VIEW their timesheet, SUBMIT it for approval, and since Session ANF LOG AND REMOVE
// THEIR OWN TRAVEL. They still cannot edit any other entry; the office does that on their behalf
// (server/src/modules/timesheets/timesheet.routes.ts).
//
// Mirrors historyStore.ts (Session AEK): the pure caching policy (readThroughCache, isFresh,
// isAuthFailure, CacheReadResult) already lives in historyCache.ts and is fully generic, so it is
// reused here rather than duplicated; the IndexedDB adapter lives in db.ts; this file is the one
// thing the React layer calls.
//
// READ-THROUGH CACHE ONLY: this file caches READS. Every write from this screen (submitting, and
// since Session ANF logging and removing travel) goes straight to the server and is never queued.
// There is no timesheet outbox, so the screen gates those controls on being online rather than
// accepting a tap it would silently lose. A week viewed once is still readable with no signal,
// clearly labelled when what is shown is saved rather than live.
import { api } from "../api";
import type { TimesheetSheet } from "../api";
import { timesheetCacheStore } from "./db";
import { readThroughCache } from "./historyCache";
import type { CacheReadResult } from "./historyCache";
import { wipeGeneration } from "./wipeEpoch";

// The engineer's own status can change from the office side (an approval, or a correction) at any
// time, so this is deliberately short: long enough that stepping between weeks already visited
// feels instant, short enough that a status change is noticed on the next ordinary visit to the tab.
export const TIMESHEET_TTL_MS = 15 * 60 * 1000;

// weekStarting is the Monday (YYYY-MM-DD) being asked for; omitted means "the current week", which
// is cached under its own key so it never collides with an explicitly-dated week, including the
// current week's own date once the engineer has stepped away and back.
export function timesheetCacheKey(weekStarting?: string): string {
  return `engineer:timesheet:${weekStarting ?? "current"}`;
}

// The one read. weekStarting omitted calls `api.timesheets.sheet()` with no arguments, which the
// server resolves to the engineer's own current week; a YYYY-MM-DD steps to a specific week.
// Engineer id is never passed: the server already knows who is asking.
//
// Session ASB review (I6-6): a week whose answer lands after this browser's offline copies were wiped (a sign-out
// while the request was in flight) is not written back to the device (wipeEpoch.ts).
export function loadTimesheet(weekStarting?: string): Promise<CacheReadResult<TimesheetSheet>> {
  const started = wipeGeneration();
  const store = {
    ...timesheetCacheStore,
    putEntry: async (entry: Parameters<typeof timesheetCacheStore.putEntry>[0]) => {
      if (wipeGeneration() !== started) return;
      await timesheetCacheStore.putEntry(entry);
    },
  };
  return readThroughCache(store, timesheetCacheKey(weekStarting), TIMESHEET_TTL_MS, () =>
    api.timesheets.sheet(weekStarting ? { weekStarting } : undefined),
  );
}

// Drop every cached week. Wired to every auth boundary: src/lib/offlineCache.ts imports this and
// awaits it inside clearOfflineApiCache, alongside the existing clearHistoryCache() call there, so
// a shared-device engineer sign-out never leaves a previous engineer's cached hours readable. Also
// called after a successful submit (MyTimesheet.tsx), so a superseded pre-submit status is never
// served back from a still-fresh cache entry as if it were live.
export function clearTimesheetCache(): Promise<void> {
  return timesheetCacheStore.clear();
}
